import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors (skip login — invalid credentials should not hard-redirect)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isLoginRequest = requestUrl.includes('/auth/login');
      const onLoginPage = window.location.pathname === '/admin/login';

      if (!isLoginRequest) {
        localStorage.removeItem('admin_token');
        if (!onLoginPage && window.location.pathname.startsWith('/admin')) {
          window.location.href = '/admin/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (username, password) => 
  api.post('/auth/login', { username, password });

export const verifyAuth = () => api.get('/auth/verify');

export const changePassword = (currentPassword, newPassword) =>
  api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });

// Categories
export const getCategories = () => api.get('/categories');
export const getNavigationCategories = () => api.get('/categories/navigation');
export const getCategory = (slug) => api.get(`/categories/${slug}`);
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// Brands
export const getBrands = (categoryId) => 
  api.get('/brands', { params: categoryId ? { category_id: categoryId } : {} });
export const getHomeCarouselBrands = () => api.get('/brands/home/carousel');
export const getBrand = (slug) => api.get(`/brands/${slug}`);
export const createBrand = (data) => api.post('/brands', data);
export const updateBrand = (id, data) => api.put(`/brands/${id}`, data);
export const deleteBrand = (id) => api.delete(`/brands/${id}`);
export const migrateIndependentBrands = () => api.post('/admin/migrate-independent-brands');

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProductsAdminFacets = () => api.get('/products/admin-facets');
export const getProduct = (slug) => api.get(`/products/${slug}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const exportProductsExcel = () =>
  api.get('/products/export/xlsx', { responseType: 'blob' });
export const downloadSampleProductsExcel = () =>
  api.get('/products/sample/xlsx', { responseType: 'blob' });
export const importProductsExcel = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/products/import/xlsx', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Leads
export const createLead = (data) => api.post('/leads', data);
export const getLeads = (params) => api.get('/leads', { params });
export const updateLeadStatus = (id, status) => api.put(`/leads/${id}/status?status=${status}`);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const getLeadStats = () => api.get('/leads/stats');

// Catalog Downloads
export const createCatalogDownload = (data) => api.post('/catalog-downloads', data);
export const getCatalogDownloads = (params) => api.get('/catalog-downloads', { params });
export const deleteCatalogDownload = (id) => api.delete(`/catalog-downloads/${id}`);

// Blogs
export const getBlogs = (publishedOnly = true) => 
  api.get('/blogs', { params: { published_only: publishedOnly } });
export const getBlog = (slug) => api.get(`/blogs/${slug}`);
export const createBlog = (data) => api.post('/blogs', data);
export const updateBlog = (id, data) => api.put(`/blogs/${id}`, data);
export const deleteBlog = (id) => api.delete(`/blogs/${id}`);

// FAQs
export const getFaqs = () => api.get('/faqs');
export const createFaq = (data) => api.post('/faqs', data);
export const updateFaq = (id, data) => api.put(`/faqs/${id}`, data);
export const deleteFaq = (id) => api.delete(`/faqs/${id}`);

// Stats
export const getStats = () => api.get('/stats');

// Seed
export const seedDatabase = () => api.post('/seed');

// Setup Admin
export const setupAdmin = () => api.post('/setup-admin');

// Upload — kind: 'product' | 'category' | 'brand' | 'blog' (stored under uploads/<kind>/ on the server)
export const uploadImage = (file, kind = 'product') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('kind', kind);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export default api;
